"use client";

import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Api from "../../api/Api";
import Breadcrumbs from "../../components/Common/Breadcrumb";

export default function WelcomeImages() {
  const token = useSelector((state) => state.login.token);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    image_url: "",
    order: 1,
    is_active: true,
  });
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.get("/admin/welcome-images");
      if (response.data.success) {
        setImages(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Failed to fetch welcome images");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setImageFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("subtitle", formData.subtitle);
      submitData.append("order", formData.order);
      submitData.append("is_active", formData.is_active ? 1 : 0);

      if (imageFile) {
        submitData.append("image", imageFile);
      } else if (formData.image_url) {
        submitData.append("image_url", formData.image_url);
      }

      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.post("/admin/welcome-images", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success("Welcome image added successfully");
        setModal(false);
        resetForm();
        fetchImages();
      }
    } catch (error) {
      console.error("Error adding image:", error);
      toast.error(error.response?.data?.message || "Failed to add image");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (image) => {
    setSelectedImage(image);
    setFormData({
      title: image.title || "",
      subtitle: image.subtitle || "",
      image_url: image.image_url || "",
      order: image.order || 1,
      is_active: image.is_active,
    });
    setEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("subtitle", formData.subtitle);
      submitData.append("order", formData.order);
      submitData.append("is_active", formData.is_active ? 1 : 0);
      submitData.append("_method", "PUT");

      if (imageFile) {
        submitData.append("image", imageFile);
      } else if (formData.image_url) {
        submitData.append("image_url", formData.image_url);
      }

      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.post(
        `/admin/welcome-images/${selectedImage.id}`,
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success("Welcome image updated successfully");
        setEditModal(false);
        resetForm();
        fetchImages();
      }
    } catch (error) {
      console.error("Error updating image:", error);
      toast.error(error.response?.data?.message || "Failed to update image");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.delete(
        `/admin/welcome-images/${selectedImage.id}`
      );
      if (response.data.success) {
        toast.success("Welcome image deleted successfully");
        setDeleteModal(false);
        setSelectedImage(null);
        fetchImages();
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  const toggleStatus = async (image) => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.put(
        `/admin/welcome-images/${image.id}/status`,
        {
          is_active: !image.is_active,
        }
      );
      if (response.data.success) {
        toast.success(
          `Image ${!image.is_active ? "activated" : "deactivated"} successfully`
        );
        fetchImages();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update image status");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      subtitle: "",
      image_url: "",
      order: 1,
      is_active: true,
    });
    setImageFile(null);
    setSelectedImage(null);
  };

  const closeModal = () => {
    setModal(false);
    resetForm();
  };

  const closeEditModal = () => {
    setEditModal(false);
    resetForm();
  };

  if (loading) {
    return (
      <Container fluid>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <Spinner size="lg" color="primary" />
        </div>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Welcome Images" breadcrumbItem="Welcome Images" />
          <Row>
            <Col>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title mb-0">Welcome Screen Images</h4>
                    <p className="text-muted mb-0">
                      Manage images displayed on the app welcome screen
                    </p>
                  </div>
                  <Button
                    color="primary"
                    onClick={() => setModal(true)}
                    className="btn-sm"
                  >
                    <i className="ri-add-line me-1"></i>
                    Add New Image
                  </Button>
                </CardHeader>
                <CardBody>
                  {images.length === 0 ? (
                    <Alert color="info" className="text-center">
                      <i className="ri-information-line me-2"></i>
                      No welcome images found. Add your first image to get
                      started.
                    </Alert>
                  ) : (
                    <Row>
                      {images.map((image) => (
                        <Col lg={4} md={6} key={image.id} className="mb-4">
                          <Card className="h-100">
                            <div className="position-relative">
                              <img
                                src={image.image_url || "/placeholder.svg"}
                                alt={image.title}
                                className="card-img-top"
                                style={{
                                  height: "200px",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null; // prevent infinite loop
                                  e.currentTarget.src = "/placeholder.svg";
                                }}
                              />
                              <div className="position-absolute top-0 end-0 p-2">
                                <Badge
                                  color={
                                    image.is_active ? "success" : "secondary"
                                  }
                                  className="fs-12"
                                >
                                  {image.is_active ? "Active" : "Inactive"}
                                </Badge>
                              </div>
                            </div>
                            <CardBody>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="card-title mb-1">
                                  {image.title || "Untitled"}
                                </h6>
                                <Badge color="info" className="fs-12">
                                  Order: {image.order}
                                </Badge>
                              </div>
                              {image.subtitle && (
                                <p className="text-muted small mb-3">
                                  {image.subtitle}
                                </p>
                              )}
                              <div className="d-flex gap-2">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEdit(image)}
                                >
                                  <i className="ri-edit-line"></i>
                                </Button>
                                <Button
                                  color={
                                    image.is_active ? "warning" : "success"
                                  }
                                  size="sm"
                                  onClick={() => toggleStatus(image)}
                                >
                                  <i
                                    className={
                                      image.is_active
                                        ? "ri-pause-circle-line"
                                        : "ri-play-circle-line"
                                    }
                                  ></i>
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedImage(image);
                                    setDeleteModal(true);
                                  }}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Add Image Modal */}
          <Modal isOpen={modal} toggle={closeModal} size="lg">
            <Form onSubmit={handleSubmit}>
              <ModalHeader toggle={closeModal}>
                Add New Welcome Image
              </ModalHeader>
              <ModalBody>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="title">Title</Label>
                      <Input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter image title"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="subtitle">Subtitle</Label>
                      <Input
                        type="text"
                        id="subtitle"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleInputChange}
                        placeholder="Enter image subtitle"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="order">Display Order</Label>
                      <Input
                        type="number"
                        id="order"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="1"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mt-4">
                      <Input
                        type="checkbox"
                        id="is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      <Label check for="is_active">
                        Active
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="image">Upload Image</Label>
                  <Input
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <small className="text-muted">
                    Supported formats: JPG, PNG, GIF. Max size: 5MB
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="image_url">Or Image URL</Label>
                  <Input
                    type="url"
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                  />
                  <small className="text-muted">
                    You can either upload an image or provide a URL
                  </small>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Adding...
                    </>
                  ) : (
                    "Add Image"
                  )}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Edit Image Modal */}
          <Modal isOpen={editModal} toggle={closeEditModal} size="lg">
            <Form onSubmit={handleUpdate}>
              <ModalHeader toggle={closeEditModal}>
                Edit Welcome Image
              </ModalHeader>
              <ModalBody>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit_title">Title</Label>
                      <Input
                        type="text"
                        id="edit_title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter image title"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit_subtitle">Subtitle</Label>
                      <Input
                        type="text"
                        id="edit_subtitle"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleInputChange}
                        placeholder="Enter image subtitle"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit_order">Display Order</Label>
                      <Input
                        type="number"
                        id="edit_order"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="1"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mt-4">
                      <Input
                        type="checkbox"
                        id="edit_is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      <Label check for="edit_is_active">
                        Active
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                {selectedImage && (
                  <FormGroup>
                    <Label>Current Image</Label>
                    <div>
                      <img
                        src={selectedImage.image_url || "/placeholder.svg"}
                        alt="Current"
                        style={{
                          maxWidth: "200px",
                          height: "auto",
                          borderRadius: "4px",
                        }}
                        onError={(e) => {
                          e.currentTarget.onerror = null; // prevent infinite loop
                          e.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    </div>
                  </FormGroup>
                )}
                <FormGroup>
                  <Label for="edit_image">Upload New Image</Label>
                  <Input
                    type="file"
                    id="edit_image"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <small className="text-muted">
                    Leave empty to keep current image
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="edit_image_url">Or Image URL</Label>
                  <Input
                    type="url"
                    id="edit_image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={closeEditModal}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    "Update Image"
                  )}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            isOpen={deleteModal}
            toggle={() => setDeleteModal(false)}
            centered
          >
            <ModalHeader toggle={() => setDeleteModal(false)}>
              Confirm Delete
            </ModalHeader>
            <ModalBody>
              <div className="text-center">
                <i
                  className="ri-delete-bin-line text-danger"
                  style={{ fontSize: "48px" }}
                ></i>
                <h5 className="mt-3">Are you sure?</h5>
                <p className="text-muted">
                  Do you want to delete this welcome image? This action cannot
                  be undone.
                </p>
                {selectedImage && (
                  <div className="mt-3">
                    <img
                      src={selectedImage.image_url || "/placeholder.svg"}
                      alt="To delete"
                      style={{
                        maxWidth: "150px",
                        height: "auto",
                        borderRadius: "4px",
                      }}
                      onError={(e) => {
                        e.currentTarget.onerror = null; // prevent infinite loop
                        e.currentTarget.src = "/placeholder.svg";
                      }}
                    />
                    <p className="mt-2 fw-medium">{selectedImage.title}</p>
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setDeleteModal(false)}>
                Cancel
              </Button>
              <Button color="danger" onClick={handleDelete}>
                <i className="ri-delete-bin-line me-1"></i>
                Delete
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
}
